/**
 * Low-level encoding helpers shared across the kit.
 */
import { Buffer } from "buffer";


const U128_MASK = (1n << 128n) - 1n;

export function utf8ToBytes(value: string): Uint8Array {
  return new Uint8Array(Buffer.from(value, "utf8"));
}

export function bytesToUtf8(value: Uint8Array): string {
  return Buffer.from(value).toString("utf8");
}

export function bytesToBase64Url(bytes: Uint8Array): string {
  return Buffer.from(bytes)
    .toString("base64")
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");
}

export function base64UrlToBytes(value: string): Uint8Array {
  const normal = value.replace(/-/g, "+").replace(/_/g, "/");
  const padded = normal.padEnd(Math.ceil(normal.length / 4) * 4, "=");
  return new Uint8Array(Buffer.from(padded, "base64"));
}

export function secureRandomBytes(length: number): Uint8Array {
  if (!globalThis.crypto?.getRandomValues) throw new Error("kit: secure random source unavailable");
  const out = new Uint8Array(length);
  globalThis.crypto.getRandomValues(out);
  return out;
}

/** Split a u256 into the `[low, high]` felt pair Cairo expects. */
export function u256ToFelts(value: bigint): [bigint, bigint] {
  return [value & U128_MASK, value >> 128n];
}

/** Big-endian bytes -> bigint. */
export function bytesToBigInt(bytes: Uint8Array): bigint {
  let out = 0n;
  for (const b of bytes) out = (out << 8n) | BigInt(b);
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  let s = "0x";
  for (const b of bytes) s += b.toString(16).padStart(2, "0");
  return s;
}

export function hexToBytes(hex: string): Uint8Array {
  const clean = hex.startsWith("0x") ? hex.slice(2) : hex;
  const padded = clean.length % 2 ? "0" + clean : clean;
  const out = new Uint8Array(padded.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(padded.slice(i * 2, i * 2 + 2), 16);
  return out;
}

/** A felt/bigint -> 32-byte big-endian Uint8Array (the tx-hash width). */
export function bigIntTo32Bytes(value: bigint): Uint8Array {
  const out = new Uint8Array(32);
  let v = value;
  for (let i = 31; i >= 0; i--) {
    out[i] = Number(v & 0xffn);
    v >>= 8n;
  }
  return out;
}
